import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiClientError } from '@/lib/api';
import { authApi } from '@/lib/endpoints';
import { ru } from '@/i18n/ru';

interface AuthContextValue {
  username: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginError: string | null;
  clearLoginError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [loginError, setLoginError] = useState<string | null>(null);

  const sessionQuery = useQuery({
    queryKey: ['auth', 'session'],
    queryFn: () => authApi.session(),
    staleTime: 60_000,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      authApi.login(username, password),
    onSuccess: async (data) => {
      setLoginError(null);
      queryClient.setQueryData(['auth', 'session'], {
        authenticated: true,
        username: data.user.username,
      });
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
    onError: (err) => {
      if (err instanceof ApiClientError && err.status === 429) {
        setLoginError(ru.tooManyAttempts);
      } else {
        setLoginError(ru.invalidCredentials);
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: async () => {
      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
    },
  });

  const login = useCallback(
    async (username: string, password: string) => {
      setLoginError(null);
      await loginMutation.mutateAsync({ username, password });
    },
    [loginMutation],
  );

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const value = useMemo<AuthContextValue>(() => {
    const data = sessionQuery.data;
    return {
      username: data?.username ?? null,
      isAuthenticated: Boolean(data?.authenticated),
      isLoading: sessionQuery.isLoading,
      login,
      logout,
      loginError,
      clearLoginError: () => setLoginError(null),
    };
  }, [sessionQuery.data, sessionQuery.isLoading, login, logout, loginError]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
